import React, { useState } from "react";
import {
  FaChevronDown,
  FaFilter,
  FaMapMarkedAlt,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import { departementsEtRegions } from "../../../../utils/departements";
import {
  SHARED_EVENT_CATEGORIES,
  SHARED_EVENT_LEVELS,
  SHARED_EVENT_TYPES,
} from "../utils/sharedEventsUtils";

function SelectField({ label, name, value, onChange, options, placeholder }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>

      <select
        name={name}
        value={value}
        onChange={(event) => onChange(name, event.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
      >
        <option value="">{placeholder}</option>

        {options.map((option) => {
          if (typeof option === "string") {
            return (
              <option key={option} value={option}>
                {option}
              </option>
            );
          }

          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function SharedEventsToolbar({
  filters,
  onFilterChange,
  onReset,
  activeFiltersCount = 0,
  resultsCount = 0,
  totalCount = 0,
  onOpenMap,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const departmentOptions = departementsEtRegions.map((dep) => ({
    value: dep.code,
    label: `${dep.code} - ${dep.departement}`,
  }));

  return (
    <div className="sticky top-[88px] z-30 rounded-[2rem] border border-slate-200 bg-white/95 p-4 shadow-xl shadow-slate-200/60 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/20 lg:top-[104px]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">
            {resultsCount} résultat{resultsCount > 1 ? "s" : ""} affiché
            {resultsCount > 1 ? "s" : ""} / {totalCount}
          </p>

          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
            Filtre rapidement les événements partagés disponibles.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={onOpenMap}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5"
          >
            <FaMapMarkedAlt />
            Carte
          </button>

          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
          >
            <FaFilter />
            Filtres
            {activeFiltersCount > 0 && (
              <span className="rounded-full bg-orange-500 px-2 py-0.5 text-xs text-white">
                {activeFiltersCount}
              </span>
            )}
            <FaChevronDown
              className={`transition ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      <div className="mt-4">
        <label className="relative block">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={filters.search}
            onChange={(event) => onFilterChange("search", event.target.value)}
            placeholder="Rechercher par titre, lieu, description..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:focus:border-sky-300/50 dark:focus:bg-slate-950 dark:focus:ring-sky-500/10"
          />
        </label>
      </div>

      {isOpen && (
        <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Période
              </span>

              <select
                name="time_event"
                value={filters.time_event}
                onChange={(event) =>
                  onFilterChange("time_event", event.target.value)
                }
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              >
                <option value="all">Tous</option>
                <option value="upcoming">À venir</option>
                <option value="past">Passés</option>
              </select>
            </label>

            <SelectField
              label="Département"
              name="departement"
              value={filters.departement}
              onChange={onFilterChange}
              placeholder="Tous les départements"
              options={departmentOptions}
            />

            <SelectField
              label="Type"
              name="type_event"
              value={filters.type_event}
              onChange={onFilterChange}
              placeholder="Tous les types"
              options={SHARED_EVENT_TYPES}
            />

            <SelectField
              label="Catégorie"
              name="cate_event"
              value={filters.cate_event}
              onChange={onFilterChange}
              placeholder="Toutes les catégories"
              options={SHARED_EVENT_CATEGORIES}
            />

            <SelectField
              label="Niveau"
              name="level_event"
              value={filters.level_event}
              onChange={onFilterChange}
              placeholder="Tous les niveaux"
              options={SHARED_EVENT_LEVELS}
            />
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-slate-950 dark:text-white"
            >
              <FaTimes />
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SharedEventsToolbar;